import React, { useState } from 'react';

const GrammarUnit13Content = ({ unitData, userPreferences, onMarkComplete, userProgress }) => {
  const [currentSection, setCurrentSection] = useState('overview');

  // Safety check for unit data
  if (!unitData || !unitData.content) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
        <div className="max-w-4xl mx-auto">
          <div className="bg-gray-800/30 rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">No Content Available</h2>
            <p className="text-gray-300">Content for Unit 13 is not available.</p>
          </div>
        </div>
      </div>
    );
  }

  const { content } = unitData;

  const renderOverview = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">進階文法結構概覽</h3>
        <p className="text-gray-300 mb-6">{content.overview.description}</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {content.overview.structures.map((structure, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{structure.name}</h4>
              <p className="text-yellow-300 font-mono mb-2">{structure.examples}</p>
              <p className="text-gray-300">{structure.description}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderInversion = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">倒裝句</h3>
        <p className="text-gray-300 mb-6">{content.inversion.description}</p>
        
        <div className="space-y-6">
          {content.inversion.types.map((type, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-3 text-green-300">{type.type}</h4>
              <p className="text-gray-300 mb-3">{type.explanation}</p>
              <div className="space-y-2">
                {type.examples.map((example, idx) => (
                  <div key={idx} className="bg-gray-600/30 p-3 rounded">
                    <p className="text-blue-300 text-sm">{example.normal}</p>
                    <p className="text-yellow-300 text-sm">{example.inverted}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderEmphasis = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">強調句</h3>
        <p className="text-gray-300 mb-6">{content.emphasis?.description || ''}</p>
        
        <div className="space-y-4">
          {content.emphasis?.types?.map((type, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{type.type}</h4>
              <p className="text-yellow-300 mb-2">{type.description}</p>
              <div className="space-y-2">
                {type.examples.map((example, idx) => (
                  <div key={idx} className="bg-gray-600/30 p-3 rounded">
                    {example.original && (
                      <p className="text-blue-300 text-sm">原句: {example.original}</p>
                    )}
                    {example.emphasized_subject && (
                      <p className="text-yellow-300 text-sm">強調主詞: {example.emphasized_subject}</p>
                    )}
                    {example.emphasized_object && (
                      <p className="text-yellow-300 text-sm">強調受詞: {example.emphasized_object}</p>
                    )}
                    {example.emphasized_place && (
                      <p className="text-yellow-300 text-sm">強調地點: {example.emphasized_place}</p>
                    )}
                    {example.emphasized_time && (
                      <p className="text-yellow-300 text-sm">強調時間: {example.emphasized_time}</p>
                    )}
                    {example.emphasized && (
                      <p className="text-yellow-300 text-sm">強調句: {example.emphasized}</p>
                    )}
                    {example.explanation && (
                      <p className="text-gray-400 text-sm">說明: {example.explanation}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderEllipsis = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">省略句</h3>
        <p className="text-gray-300 mb-6">{content.ellipsis?.description || ''}</p>
        
        <div className="space-y-4">
          {content.ellipsis?.types?.map((type, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{type.type}</h4>
              <p className="text-yellow-300 mb-2">{type.description}</p>
              <div className="space-y-2">
                {type.examples.map((example, idx) => (
                  <div key={idx} className="bg-gray-600/30 p-3 rounded">
                    {example.complete && (
                      <p className="text-blue-300 text-sm">完整句: {example.complete}</p>
                    )}
                    {example.ellipsis && (
                      <p className="text-yellow-300 text-sm">省略句: {example.ellipsis}</p>
                    )}
                    {example.question && (
                      <p className="text-blue-300 text-sm">問題: {example.question}</p>
                    )}
                    {example.complete_answer && (
                      <p className="text-blue-300 text-sm">完整回答: {example.complete_answer}</p>
                    )}
                    {example.ellipsis_answer && (
                      <p className="text-yellow-300 text-sm">省略回答: {example.ellipsis_answer}</p>
                    )}
                    {example.explanation && (
                      <p className="text-gray-400 text-sm">說明: {example.explanation}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderComplexStructures = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">複雜句型</h3>
        <p className="text-gray-300 mb-6">{content.complex_structures?.description || ''}</p>
        
        <div className="space-y-6">
          {content.complex_structures?.types?.map((type, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-3 text-green-300">{type.type}</h4>
              <p className="text-gray-300 mb-3">{type.description}</p>
              <div className="space-y-2">
                {type.examples.map((example, idx) => (
                  <div key={idx} className="bg-gray-600/30 p-3 rounded">
                    {example.structure && (
                      <p className="text-blue-300 text-sm">結構: {example.structure}</p>
                    )}
                    {example.example && (
                      <p className="text-yellow-300 text-sm">例句: {example.example}</p>
                    )}
                    {example.type && (
                      <p className="text-blue-300 text-sm">類型: {example.type}</p>
                    )}
                    {example.explanation && (
                      <p className="text-gray-400 text-sm">說明: {example.explanation}</p>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSpecialPatterns = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">特殊句型</h3>
        <p className="text-gray-300 mb-6">{content.special_patterns?.description || ''}</p>
        
        <div className="space-y-4">
          {content.special_patterns?.patterns?.map((pattern, index) => (
            <div key={index} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-2 text-green-300">{pattern.type}</h4>
              <p className="text-yellow-300 mb-2">{pattern.description}</p>
              <div className="space-y-1">
                {pattern.examples.map((example, idx) => (
                  <p key={idx} className="text-blue-300 text-sm">{example}</p>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderExercises = () => (
    <div className="space-y-6">
      <div className="bg-gray-800/30 rounded-xl p-6">
        <h3 className="text-xl font-bold mb-4 text-blue-300">練習題</h3>
        
        <div className="space-y-6">
          {content.exercises.sections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="bg-gray-700/50 p-4 rounded-lg">
              <h4 className="text-lg font-semibold mb-3 text-green-300">{section.name}</h4>
              
              <div className="space-y-4">
                {section.questions.map((question, questionIndex) => (
                  <div key={questionIndex} className="bg-gray-600/30 p-3 rounded">
                    <p className="text-gray-300 mb-3">{question.question}</p>
                    <div className="space-y-2">
                      {Object.entries(question.options).map(([key, option]) => (
                        <div key={key} className="flex items-center">
                          <span className="text-yellow-300 font-mono mr-3">{key}.</span>
                          <span className="text-gray-300">{option}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderNavigation = () => (
    <div className="bg-gray-800/30 rounded-xl p-4 mb-6">
      <div className="flex flex-wrap gap-2">
        <button
          onClick={() => setCurrentSection('overview')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'overview'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          概覽
        </button>
        <button
          onClick={() => setCurrentSection('inversion')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'inversion'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          倒裝句
        </button>
        <button
          onClick={() => setCurrentSection('emphasis')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'emphasis'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          強調句
        </button>
        <button
          onClick={() => setCurrentSection('ellipsis')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'ellipsis'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          省略句
        </button>
        <button
          onClick={() => setCurrentSection('complex')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'complex'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          複雜句型
        </button>
        <button
          onClick={() => setCurrentSection('special')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'special'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          特殊句型
        </button>
        <button
          onClick={() => setCurrentSection('exercises')}
          className={`px-4 py-2 rounded-lg transition-colors ${
            currentSection === 'exercises'
              ? 'bg-blue-600 text-white'
              : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          練習題
        </button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-900 via-blue-900 to-indigo-900 text-white p-6">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="bg-gray-800/30 rounded-xl p-6 mb-6">
          <h1 className="text-3xl font-bold mb-2 text-blue-300">
            {unitData.title[userPreferences?.language || 'traditional']}
          </h1>
          <p className="text-gray-300 mb-4">
            {unitData.subtitle[userPreferences?.language || 'traditional']}
          </p>
          <p className="text-gray-400">
            {unitData.description[userPreferences?.language || 'traditional']}
          </p>
        </div>

        {/* Navigation */}
        {renderNavigation()}

        {/* Content */}
        {currentSection === 'overview' && renderOverview()}
        {currentSection === 'inversion' && renderInversion()}
        {currentSection === 'emphasis' && renderEmphasis()}
        {currentSection === 'ellipsis' && renderEllipsis()}
        {currentSection === 'complex' && renderComplexStructures()}
        {currentSection === 'special' && renderSpecialPatterns()}
        {currentSection === 'exercises' && renderExercises()}

        {/* Complete Button */}
        <div className="mt-8 text-center">
          <button
            onClick={() => onMarkComplete && onMarkComplete(unitData.id)}
            className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            標記為完成
          </button>
        </div>
      </div>
    </div>
  );
};

export default GrammarUnit13Content;
